import { parseUrlToIndexUiState } from '../search-router/url-handling';
import {
  MIN_SEARCH_QUERY_CHARS,
  shouldSkipPersistingSavedSearchUrl,
  stripEphemeralSearchParams,
  toStoredSearchUrl,
} from './normalize-saved-search-url';

/**
 * What counts as a search on the Searches & Alerts hub.
 *
 * The SRP records every settled query/filter change into the saved-searches list, which is the
 * right raw signal (the hub's whole premise is "you don't have to remember to save") but a
 * terrible list to show as-is: keystroke fragments ("Ju", "Jun", "Junio"), filter walks where
 * five chips are added and removed in a minute, a salary slider dragged through seven values,
 * the same search re-run with a different sort or page, and so on. Real account data from
 * 2026-09-25 had users with 144 stored items of which perhaps a dozen were searches they would
 * recognise.
 *
 * This module is the one place that decides which recorded URLs are searches:
 *
 * 1. `searchIdentityKey`: two URLs are the same search when they parse to the same query and
 *    the same refinements (case-insensitive), ignoring sort, page, page size, map view and
 *    absolute "since my last visit" timestamps. `Helsinki_Finland` and `helsinki_finland`,
 *    `?jobType=full-time` and `?jobType=full-time&sort=date&page=3` are one search each.
 * 2. `areRelatedSearches`: an earlier URL is a *transitional step* toward a later one when the
 *    later one is the same search with the query extended or shortened (typing), with exactly
 *    one filter added, removed or re-valued (one chip click, one slider drag), or with the typed
 *    query promoted into a facet ("helsi" -> location Helsinki). Two unrelated searches run back
 *    to back ("nurse", then "teacher") are both real.
 * 3. `distillSearchHistory`: an entry survives when it is pinned (deliberately saved: alert on,
 *    named, or filed in a folder), or when the user stayed on it for at least
 *    `TRANSIENT_DWELL_MS` before moving on, or when the next thing they did was unrelated to it,
 *    or when it is the last thing they did. Everything else was a step on the way to a search,
 *    not a search.
 *
 * Pure and synchronous: used by the redux reducer at record time (so the list never holds the
 * garbage), by the account-sync merge (so other devices' history is distilled the same way), and
 * by the hub page at render time (so history recorded before this policy existed is cleaned up
 * without touching anyone's account data).
 */

/**
 * How long a user has to stay on a search before it counts as one even if their next move
 * refined it. Below this, an add-one-chip or extend-the-query change is treated as the same
 * in-progress search. Two minutes is long enough to have scrolled the first page of results.
 */
export const TRANSIENT_DWELL_MS = 2 * 60 * 1000;

export interface HistoryEntryLike {
  url: string;
  timestamp: number;
  /** Deliberately saved (alert CTA, suggestion accepted, named, foldered): never collapsed. */
  pinned?: boolean;
}

interface ParsedSearch {
  /** Lower-cased, trimmed query. */
  query: string;
  /** Refinement attribute -> sorted, lower-cased, de-duplicated values. Global scope is folded in as `_scope`. */
  refinements: Map<string, string[]>;
}

function parseSearch(url: string): ParsedSearch {
  const stripped = stripEphemeralSearchParams(url);
  const state = parseUrlToIndexUiState(stripped);
  const refinements = new Map<string, string[]>();
  for (const [attribute, raw] of Object.entries(state.refinementList ?? {})) {
    if (!Array.isArray(raw)) continue;
    const values = Array.from(
      new Set(raw.filter((v) => v != null && String(v).trim() !== '').map((v) => String(v).trim().toLowerCase()))
    ).sort();
    if (values.length > 0) refinements.set(attribute, values);
  }
  if (state.isFinland === false) refinements.set('_scope', ['global']);
  return { query: (state.query ?? '').trim().toLowerCase(), refinements };
}

/**
 * Stable key under which two recorded URLs are the same search. See the module comment for
 * what is and isn't part of a search's identity.
 */
export function searchIdentityKey(url: string): string {
  const parsed = parseSearch(url);
  const refinementPairs = Array.from(parsed.refinements.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([attribute, values]) => `${attribute}=${values.join('|')}`);
  return `${parsed.query}\u0000${refinementPairs.join('&')}`;
}

function sameValues(a: string[] | undefined, b: string[] | undefined): boolean {
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function differingAttributes(a: Map<string, string[]>, b: Map<string, string[]>): string[] {
  const keys = new Set([...a.keys(), ...b.keys()]);
  const differing: string[] = [];
  for (const key of keys) {
    if (!sameValues(a.get(key), b.get(key))) differing.push(key);
  }
  return differing;
}

function isTypingStep(earlier: string, later: string): boolean {
  if (earlier === later) return true;
  // A query below the minimum is a keystroke whatever it says: "e" -> "chemic", "do" -> "bioprocess".
  if (earlier.length < MIN_SEARCH_QUERY_CHARS + 1 || later.length < MIN_SEARCH_QUERY_CHARS + 1) return true;
  return later.startsWith(earlier) || earlier.startsWith(later);
}

/**
 * True when `later` is a refinement of `earlier` rather than a different search. Symmetric in the
 * typing and one-filter cases, directional in the promotion case (typed text became a facet).
 */
export function areRelatedSearches(earlierUrl: string, laterUrl: string): boolean {
  const earlier = parseSearch(earlierUrl);
  const later = parseSearch(laterUrl);
  const changed = differingAttributes(earlier.refinements, later.refinements);

  if (changed.length === 0) {
    return isTypingStep(earlier.query, later.query);
  }

  if (earlier.query === later.query) {
    // One chip added, removed or re-valued; one slider moved; scope toggled.
    return changed.length === 1;
  }

  // The typed text was promoted into a facet ("helsi" -> location Helsinki; "process" ->
  // title Process engineer), or a facet was picked after one or two keystrokes (a suggestion
  // click). Only added or re-valued attributes can carry the promoted text.
  if (earlier.query.length > 0 && later.query.length === 0) {
    if (earlier.query.length < MIN_SEARCH_QUERY_CHARS + 1) return changed.length === 1;
    return changed.some((attribute) => {
      const values = later.refinements.get(attribute);
      if (!values) return false;
      return values.some((value) => value.startsWith(earlier.query) || earlier.query.startsWith(value));
    });
  }

  // Query typed while one filter was also changed: still one in-progress search when the query
  // part reads as typing, i.e. an extension of what was there, or a fresh query started right
  // after a chip was removed (the SRP's "narrow it differently" move).
  if (changed.length !== 1 || later.query.length === 0) return false;
  return earlier.query.length === 0 || isTypingStep(earlier.query, later.query);
}

export interface DistillOptions<T extends HistoryEntryLike> {
  /** Extra pin source beyond `entry.pinned`, e.g. the account item's alert/label/folder state. */
  isPinned?: (entry: T) => boolean;
}

/**
 * Reduces a recorded search history (any order) to the entries that count as searches, newest
 * first. Deduplicates by `searchIdentityKey` (a pinned entry wins over an auto-recorded one,
 * otherwise the newest wins, and the survivor takes the newest timestamp of its group), then
 * drops every unpinned entry that the user left within `TRANSIENT_DWELL_MS` for a related
 * search. Entries whose URL is not a recordable search are dropped outright.
 */
export function distillSearchHistory<T extends HistoryEntryLike>(entries: readonly T[], options: DistillOptions<T> = {}): T[] {
  const isPinned = (entry: T): boolean => entry.pinned === true || options.isPinned?.(entry) === true;

  interface Node {
    entry: T;
    pinned: boolean;
    timestamp: number;
    /** The URL this node stands for in the result: its own, or the fuller query it was backspaced from. */
    representative: string;
  }

  const groups = new Map<string, Node>();
  for (const entry of entries) {
    if (!entry || typeof entry.url !== 'string') continue;
    const pinned = isPinned(entry);
    const stored = toStoredSearchUrl(entry.url);
    if (!stored) continue;
    // A pinned entry is shown even when it is not something the SRP would record (an alert on
    // the bare jobs index, say): the user set it up and must be able to see and switch it off.
    if (!pinned && shouldSkipPersistingSavedSearchUrl(stored)) continue;
    const timestamp = Number.isFinite(entry.timestamp) ? entry.timestamp : 0;
    const key = searchIdentityKey(stored);
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { entry, pinned, timestamp, representative: entry.url });
      continue;
    }
    const newest = Math.max(existing.timestamp, timestamp);
    const takeIncoming = pinned === existing.pinned ? timestamp > existing.timestamp : pinned;
    groups.set(
      key,
      takeIncoming ? { entry, pinned, timestamp: newest, representative: entry.url } : { ...existing, timestamp: newest }
    );
  }

  // Pairwise between chronological neighbours, iterated to a fixed point: dropping a step can
  // make two surviving entries neighbours that are themselves a step apart, and one pass would
  // leave that pair intact while a re-run of the same policy over the survivors would not.
  // Iterating means the reducer, the account-sync merge, the hub and the cleanup script all
  // agree after a single call.
  let chronological = Array.from(groups.values()).sort((a, b) => a.timestamp - b.timestamp);
  for (;;) {
    const kept: Node[] = [];
    for (let index = 0; index < chronological.length; index += 1) {
      const current = chronological[index];
      const next = chronological[index + 1];
      if (!current.pinned && next) {
        const dwell = next.timestamp - current.timestamp;
        if (dwell < TRANSIENT_DWELL_MS && areRelatedSearches(current.entry.url, next.entry.url)) {
          // The step is dropped, but when it was backspaced into the next one ("Junior" ->
          // "Junio", "biomass" -> "bio") the fuller query is what the user meant, so the
          // survivor stands for that URL instead of the truncated one it happens to hold.
          if (!next.pinned && isBackspaceOf(current.representative, next.entry.url)) {
            next.representative = current.representative;
          }
          continue;
        }
      }
      kept.push(current);
    }
    if (kept.length === chronological.length) break;
    chronological = kept;
  }

  return chronological
    .sort((a, b) => b.timestamp - a.timestamp)
    .map(({ entry, timestamp, representative }) =>
      entry.timestamp === timestamp && entry.url === representative ? entry : { ...entry, timestamp, url: representative }
    );
}

/** True when `laterUrl` is `earlierUrl` with characters deleted from the end of its query and nothing else changed. */
function isBackspaceOf(earlierUrl: string, laterUrl: string): boolean {
  const earlier = parseSearch(earlierUrl);
  const later = parseSearch(laterUrl);
  if (later.query.length === 0 || later.query.length >= earlier.query.length) return false;
  if (!earlier.query.startsWith(later.query)) return false;
  return differingAttributes(earlier.refinements, later.refinements).length === 0;
}
